import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import {
  DatePicker,
  Input,
  Modal,
  Select,
  Spinner,
} from 'UI';
import { Client, Tray } from 'constants/interface';
import { useDispatch, useSelector } from 'hooks/store';
import {
  cleanTraysAction,
  getTrayListAction,
  isLoadingTraysSelector,
  traysEntitiesSelector,
  traysIdsSelector,
} from 'state/slices/tray';
import dayjs, { Dayjs } from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import { updateEntryPrices } from 'helpers/inventory';
import {
  clientsEntitiesSelector,
  clientsIdsSelector,
  isLoadingClientsSelector,
  cleanClientsAction,
  getClientsListAction,
} from 'state/slices/clients';

import {
  FormContainer,
  BaseFormItem,
  InlineButton,
  RemoteSelectContent,
  RemoteSelectSpinnerContainer,
  QuantityFormItem,
  PriceFormItem,
  NotesInline,
  EntryItemFormRow,
  EntryItemInput,
  RemoveEntryItemButton,
  RemoveEntryItemIcon,
  AddEntryItemButton,
  FormItemRow,
} from './style';

interface EntryItemFormData {
  tray: string
  quantity: string
  unitPrice: string
  totalPrice: string
}

export interface EntryInputFormData {
  entryDate: Dayjs
  client: string
  deliveredBy: string
  receivedBy: string
  fiscalNote: string
  notes: string
  items: EntryItemFormData[]
}

interface EntryInputModalProps {
  formHandler: FormInstance
  onSubmit(data: unknown): void
  onCancel(): void
  isLoading: boolean
  isVisible: boolean
}

const EntryInputModal = ({
  formHandler,
  onSubmit,
  onCancel,
  isLoading,
  isVisible,
}: EntryInputModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const traySearchEntities = useSelector(traysEntitiesSelector);
  const traySearchIds = useSelector(traysIdsSelector);
  const isLoadingTrays = useSelector(isLoadingTraysSelector);
  const traySearchResult = traySearchIds
    .map((trayId) => traySearchEntities[trayId])
    .filter((tray) => !!tray) as Tray[];
  const [searchIndex, setSearchIndex] = useState<number>();

  const clientsEntities = useSelector(clientsEntitiesSelector);
  const clientsIds = useSelector(clientsIdsSelector);
  const isLoadingClients = useSelector(isLoadingClientsSelector);
  const clients = clientsIds
    .map((clientId) => clientsEntities[clientId])
    .filter((client) => !!client) as Client[];

  const [internalLoading, setInternalLoading] = useState(false);

  const [
    traySearchTimeout,
    setTraySearchTimeout,
  ] = useState<number>();
  const onTraySearch = (search: string, index: number) => {
    if (!search) {
      dispatch(cleanTraysAction());
      setSearchIndex(undefined);
    } else {
      setInternalLoading(true);
      setSearchIndex(index);
      window.clearTimeout(traySearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getTrayListAction({
          search,
          isPaginated: false,
        }));
        setInternalLoading(false);
      }, 500);
      setTraySearchTimeout(searchTimeout);
    }
  };

  const [
    clientSearchTimeout,
    setClientSearchTimeout,
  ] = useState<number>();
  const onClientSearch = (search: string) => {
    if (!search) {
      dispatch(cleanClientsAction());
    } else {
      setInternalLoading(true);
      window.clearTimeout(clientSearchTimeout);
      const searchTimeout = window.setTimeout(() => {
        dispatch(getClientsListAction({
          search,
          isPaginated: false,
        }));
        setInternalLoading(false);
      }, 500);
      setClientSearchTimeout(searchTimeout);
    }
  };

  useEffect(() => {
    const clearEffects = () => {
      window.clearTimeout(traySearchTimeout);
      window.clearTimeout(clientSearchTimeout);
    };
    return clearEffects;
  }, []);

  const [selectedTrays, setSelectedTrays] = useState<(Tray | undefined)[]>([]);

  const internalOnCancel = () => {
    setSelectedTrays([]);
    dispatch(cleanTraysAction());
    onCancel();
  };

  const onEntryItemAdded = (
    formAdd: (defaultValue?: any, index?: number) => void, // eslint-disable-line
    index: number,
  ) => {
    const updatedSelectedTrays = [
      ...selectedTrays.slice(0, index),
      undefined,
      ...selectedTrays.slice(index + 1),
    ];
    setSelectedTrays(updatedSelectedTrays);
    formAdd({
      trayId: undefined,
    }, index);
  };

  const onEntryItemRemoved = (formRemove: (index: number | number[]) => void, index: number) => {
    const updatedSelectedTrays = [
      ...selectedTrays.slice(0, index),
      ...selectedTrays.slice(index + 1),
    ];
    setSelectedTrays(updatedSelectedTrays);
    formRemove(index);
  };

  const onEntryItemChange = (index: number, field?: 'unit' | 'total') => {
    const currentFormItems: EntryItemFormData[] = formHandler.getFieldValue('items');
    if (!currentFormItems) {
      return;
    }

    const entryItem = currentFormItems[index];

    if (!entryItem) {
      return;
    }

    const updatedPrices = updateEntryPrices(entryItem, field);

    const updatedFormItems: typeof currentFormItems = [
      ...currentFormItems.slice(0, index),
      {
        ...entryItem,
        ...updatedPrices,
        quantity: entryItem.quantity,
      },
      ...currentFormItems.slice(index + 1),
    ];

    formHandler.setFields([{
      name: 'items',
      value: updatedFormItems,
    }]);
  };

  const onTraySelect = (tray: Tray | undefined, index: number) => {
    if (tray) {
      const updatedSelectedTrays = [
        ...selectedTrays.slice(0, index),
        { ...tray },
        ...selectedTrays.slice(index + 1),
      ];
      setSelectedTrays(updatedSelectedTrays);
      dispatch(cleanTraysAction());
      setSearchIndex(undefined);
    }
  };

  return (
    <Modal
      visible={isVisible}
      title="Nova entrada"
      destroyOnClose
      onCancel={internalOnCancel}
      afterClose={internalOnCancel}
      zIndex={90}
      width={720}
      footer={[
        <InlineButton
          key="cancel"
          onClick={internalOnCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={onSubmit}
          disabled={isLoading}
          loading={isLoading}
        >
          Salvar
        </InlineButton>,
      ]}
    >
      <FormContainer
        layout="vertical"
        form={formHandler}
        requiredMark={false}
        onFinish={onSubmit as (val: unknown) => void}
      >
        <BaseFormItem
          label="Data de entrada"
          name="entryDate"
          initialValue={dayjs()}
          rules={[
            {
              required: true,
              message: 'Insira uma data de entrada',
            },
          ]}
        >
          <DatePicker disabled={isLoading} />
        </BaseFormItem>
        <BaseFormItem
          label="Cliente"
          name="client"
          rules={[
            {
              required: true,
              message: 'Nenhum cliente selecionado',
            },
          ]}
        >
          <Select
            placeholder="Selecione um cliente"
            disabled={isLoading}
            showArrow={false}
            showSearch
            onSearch={onClientSearch}
            filterOption={false}
            notFoundContent={<></>}
            dropdownRender={(menu) => (
              <div>
                <RemoteSelectContent>
                  {menu}
                  {(internalLoading || isLoadingClients) && (
                    <RemoteSelectSpinnerContainer>
                      <Spinner />
                    </RemoteSelectSpinnerContainer>
                  )}
                </RemoteSelectContent>
              </div>
            )}
          >
            {clients.map((clientOption) => (
              <Select.Option
                value={clientOption.id}
                key={clientOption.id}
              >
                {clientOption.name}
              </Select.Option>
            ))}
          </Select>
        </BaseFormItem>
        <BaseFormItem
          label="Nota fiscal"
          name="fiscalNote"
        >
          <Input disabled={isLoading} />
        </BaseFormItem>
        <h3>Itens</h3>
        <Form.List
          name="items"
          rules={[{
            message: 'Insira pelo menos um item',
            validator: () => {
              const formItems: EntryItemFormData[] = formHandler.getFieldValue('items');
              if (formItems.length > 0) {
                return Promise.resolve();
              }

              return Promise.reject();
            },
          }]}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((itemField, index) => (
                <EntryItemFormRow
                  key={itemField.key}
                >
                  <EntryItemInput
                    label="Item"
                    style={{
                      flex: '2',
                    }}
                    name={[itemField.name, 'tray']}
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: 'Selecione um item',
                      },
                    ]}
                  >
                    <Select
                      placeholder="Selecione um item"
                      disabled={isLoading}
                      showArrow={false}
                      showSearch
                      onSearch={(searchValue) => onTraySearch(searchValue, index)}
                      filterOption={false}
                      onSelect={(
                        trayId,
                      ) => onTraySelect(traySearchEntities[trayId], index)}
                      notFoundContent={<></>}
                      dropdownRender={(menu) => (
                        <div>
                          <RemoteSelectContent>
                            {menu}
                            {(internalLoading || isLoadingTrays) && (
                              <RemoteSelectSpinnerContainer>
                                <Spinner />
                              </RemoteSelectSpinnerContainer>
                            )}
                          </RemoteSelectContent>
                        </div>
                      )}
                    >
                      {searchIndex === index
                        ? traySearchResult.map((trayOption) => (
                          <Select.Option
                            value={trayOption.id}
                            key={trayOption.id}
                          >
                            {`Bandeja ${trayOption.capacity}${trayOption.isHydroponic ? ' Hidropônica' : ''}`}
                          </Select.Option>
                        ))
                        : (() => {
                          const trayOption = selectedTrays[index];
                          return trayOption ? (
                            <Select.Option
                              value={trayOption.id}
                              key={trayOption.id}
                            >
                              {`Bandeja ${trayOption.capacity}${trayOption.isHydroponic ? ' Hidropônica' : ''}`}
                            </Select.Option>
                          ) : null;
                        })()}
                    </Select>
                  </EntryItemInput>
                  <QuantityFormItem
                    label="Quantidade"
                    name={[itemField.name, 'quantity']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma quantidade',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index)}
                      type="number"
                      disabled={isLoading || !selectedTrays[index]}
                      suffix={selectedTrays[index] ? 'Bdj' : ''}
                    />
                  </QuantityFormItem>
                  <PriceFormItem
                    label="Preço Unitário"
                    name={[itemField.name, 'unitPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um preço',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index, 'unit')}
                      type="number"
                      disabled={isLoading || !selectedTrays[index]}
                      prefix="R$"
                    />
                  </PriceFormItem>
                  <PriceFormItem
                    label="Valor"
                    name={[itemField.name, 'totalPrice']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira um preço',
                      },
                    ]}
                  >
                    <Input
                      onChange={() => onEntryItemChange(index, 'total')}
                      type="number"
                      disabled={isLoading || !selectedTrays[index]}
                      prefix="R$"
                    />
                  </PriceFormItem>
                  <RemoveEntryItemButton onClick={() => onEntryItemRemoved(remove, index)}>
                    <RemoveEntryItemIcon />
                  </RemoveEntryItemButton>
                </EntryItemFormRow>
              ))}
              <AddEntryItemButton
                type="dashed"
                onClick={() => onEntryItemAdded(add, fields.length)}
                icon={<PlusOutlined size={14} />}
              >
                Adicionar item
              </AddEntryItemButton>
            </>
          )}
        </Form.List>
        <FormItemRow>
          <BaseFormItem
            label="Entregue por"
            name="deliveredBy"
            rules={[
              {
                required: true,
                message: 'Insira um nome',
              },
            ]}
          >
            <Input disabled={isLoading} />
          </BaseFormItem>
          <BaseFormItem
            label="Recebido por"
            name="receivedBy"
            rules={[
              {
                required: true,
                message: 'Insira um nome',
              },
            ]}
          >
            <Input disabled={isLoading} />
          </BaseFormItem>
        </FormItemRow>
        <NotesInline
          label="Observações"
          name="notes"
        >
          <Input.TextArea disabled={isLoading} rows={5} />
        </NotesInline>
      </FormContainer>
    </Modal>
  );
};

export default EntryInputModal;
